import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, Search, Replace, MapPin } from 'lucide-react';
import axios from 'axios';

interface List {
  listid: number;
  name?: string;
  listname?: string;
}

interface BulkRow {
  subscriberid: number;
  listid: number;
  email: string;
  countyState: string;
  unsubscribed: number;
  status: 'active' | 'unsubscribed' | 'bounced';
}

interface Props {
  lists: List[];
}

const PAGE_SIZE = 50;

function listLabel(l: List): string {
  return l.name ?? l.listname ?? `List ${l.listid}`;
}

export default function BulkUpdate({ lists }: Props) {
  const [listId, setListId] = useState<number | ''>('');
  const [domain, setDomain] = useState('');
  const [countyFilter, setCountyFilter] = useState('');
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const lastClickedIndex = useRef<number | null>(null);

  const [newDomain, setNewDomain] = useState('');
  const [newCountyState, setNewCountyState] = useState('');
  const [applyingDomain, setApplyingDomain] = useState(false);
  const [applyingCounty, setApplyingCounty] = useState(false);
  const [resultBanner, setResultBanner] = useState<{
    kind: 'domain' | 'county';
    updated: number;
    skipped?: number;
    skippedEmails?: string[];
  } | null>(null);

  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => listLabel(a).localeCompare(listLabel(b))),
    [lists]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const search = async (append = false) => {
    if (listId === '' || !domain.trim()) {
      setError('Select a list and enter a domain.');
      return;
    }
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setResultBanner(null);
      setSelected(new Set());
      lastClickedIndex.current = null;
      setPage(0);
    }
    setError(null);
    try {
      const cursor = append && rows.length ? rows[rows.length - 1].email : '';
      const res = await axios.get('/api/interspire/utilities/bulk-update/search', {
        params: {
          listId,
          domain: domain.trim().replace(/^@/, ''),
          countyState: countyFilter.trim() || undefined,
          limit: 500,
          cursor,
        },
        timeout: 120_000,
      });
      const next: BulkRow[] = res.data?.rows ?? [];
      setHasMore(Boolean(res.data?.hasMore));
      setRows((prev) => (append ? [...prev, ...next] : next));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Search failed');
      if (!append) setRows([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleRowSelect = (
    index: number,
    e: React.MouseEvent | React.ChangeEvent,
    fromCheckbox = false
  ) => {
    const row = rows[index];
    if (!row) return;
    const native = 'nativeEvent' in e ? (e.nativeEvent as MouseEvent) : null;
    const shift = native?.shiftKey ?? false;
    const meta = Boolean(native?.ctrlKey || native?.metaKey) || fromCheckbox;

    setSelected((prev) => {
      if (shift && lastClickedIndex.current != null) {
        const next = new Set(prev);
        const a = Math.min(lastClickedIndex.current, index);
        const b = Math.max(lastClickedIndex.current, index);
        for (let i = a; i <= b; i++) next.add(rows[i].subscriberid);
        return next;
      }
      if (meta) {
        const next = new Set(prev);
        if (next.has(row.subscriberid)) next.delete(row.subscriberid);
        else next.add(row.subscriberid);
        return next;
      }
      // Plain row click: set selection to this row only
      return new Set([row.subscriberid]);
    });
    lastClickedIndex.current = index;
  };

  const selectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of pageRows) next.add(r.subscriberid);
      return next;
    });
  };

  const clearSelection = () => {
    setSelected(new Set());
    lastClickedIndex.current = null;
  };

  const applyDomain = async () => {
    if (listId === '' || selected.size === 0 || !newDomain.trim()) return;
    const count = selected.size;
    if (
      !window.confirm(
        `Change the email domain for ${count.toLocaleString()} selected contact${count === 1 ? '' : 's'} to @${newDomain.trim().replace(/^@/, '')}? Collisions on this list will be skipped.`
      )
    ) {
      return;
    }
    setApplyingDomain(true);
    setError(null);
    setResultBanner(null);
    try {
      const res = await axios.post('/api/interspire/utilities/bulk-update/domain', {
        listId,
        subscriberIds: Array.from(selected),
        newDomain: newDomain.trim(),
      });
      setResultBanner({
        kind: 'domain',
        updated: res.data.updated ?? 0,
        skipped: res.data.skipped ?? 0,
        skippedEmails: res.data.skippedEmails ?? [],
      });
      // Refresh search results
      await search(false);
      clearSelection();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Domain update failed');
    } finally {
      setApplyingDomain(false);
    }
  };

  const applyCounty = async () => {
    if (listId === '' || selected.size === 0 || !newCountyState.trim()) return;
    const count = selected.size;
    if (
      !window.confirm(
        `Set County/State to “${newCountyState.trim()}” for ${count.toLocaleString()} selected contact${count === 1 ? '' : 's'}?`
      )
    ) {
      return;
    }
    setApplyingCounty(true);
    setError(null);
    setResultBanner(null);
    try {
      const res = await axios.post('/api/interspire/utilities/bulk-update/county-state', {
        listId,
        subscriberIds: Array.from(selected),
        countyState: newCountyState.trim(),
      });
      setResultBanner({
        kind: 'county',
        updated: res.data.updated ?? 0,
      });
      await search(false);
      clearSelection();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'County/State update failed');
    } finally {
      setApplyingCounty(false);
    }
  };

  const canSearch = listId !== '' && domain.trim().length > 0 && !loading;
  const canApplyDomain = selected.size > 0 && newDomain.trim().length > 0 && !applyingDomain && !applyingCounty;
  const canApplyCounty =
    selected.size > 0 && newCountyState.trim().length > 0 && !applyingDomain && !applyingCounty;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-5 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Replace className="h-4 w-4 text-amber-500" />
          Bulk Update
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Search a domain on one list, multi-select contacts (Shift = range, Ctrl/Cmd = toggle), then
          rewrite the email domain or set County/State. Domain collisions on the same list are skipped.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            List <span className="text-red-500">*</span>
          </label>
          <select
            value={listId}
            onChange={(e) => {
              setListId(e.target.value === '' ? '' : Number(e.target.value));
              setRows([]);
              clearSelection();
            }}
            className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
          >
            <option value="">Select a list…</option>
            {sortedLists.map((l) => (
              <option key={l.listid} value={l.listid}>
                {listLabel(l)} (ID {l.listid})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Domain <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSearch) search(false);
            }}
            placeholder="oldagency.gov"
            className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            County/State filter (optional)
          </label>
          <input
            type="text"
            value={countyFilter}
            onChange={(e) => setCountyFilter(e.target.value)}
            placeholder="contains…"
            className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => search(false)}
          disabled={!canSearch}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          {loading ? 'Searching…' : 'Search'}
        </button>
        {rows.length > 0 && (
          <span className="text-sm text-gray-500 dark:text-slate-400">
            {rows.length.toLocaleString()} loaded
            {hasMore ? ' (more available)' : ''}
          </span>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => search(true)}
            disabled={loadingMore || loading}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {resultBanner && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200">
          {resultBanner.kind === 'domain' ? (
            <>
              Updated <strong>{resultBanner.updated.toLocaleString()}</strong>
              {typeof resultBanner.skipped === 'number' && (
                <>
                  {' '}
                  · Skipped <strong>{resultBanner.skipped.toLocaleString()}</strong>
                </>
              )}
              {resultBanner.skippedEmails && resultBanner.skippedEmails.length > 0 && (
                <div className="mt-1 text-xs opacity-80 truncate">
                  Sample skipped: {resultBanner.skippedEmails.slice(0, 5).join(', ')}
                  {resultBanner.skippedEmails.length > 5 ? '…' : ''}
                </div>
              )}
            </>
          ) : (
            <>
              Set County/State on <strong>{resultBanner.updated.toLocaleString()}</strong> contact
              {resultBanner.updated === 1 ? '' : 's'}
            </>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-gray-800 dark:text-slate-200">
              {selected.size.toLocaleString()} selected
            </span>
            <button type="button" onClick={selectPage} className="text-blue-600 hover:underline">
              Select page
            </button>
            <button type="button" onClick={clearSelection} className="text-gray-500 hover:underline">
              Clear
            </button>
            <span className="text-xs text-gray-400 dark:text-slate-500">
              Shift-click range · Ctrl/Cmd-click toggle
            </span>
          </div>

          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-slate-700">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/60 text-left text-xs uppercase text-gray-500 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">County/State</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {pageRows.map((r, i) => {
                  const globalIndex = page * PAGE_SIZE + i;
                  const checked = selected.has(r.subscriberid);
                  return (
                    <tr
                      key={r.subscriberid}
                      className={`cursor-pointer ${
                        checked
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-900/40'
                      }`}
                      onClick={(e) => handleRowSelect(globalIndex, e)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleRowSelect(globalIndex, e, true)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-slate-100">
                        {r.email}
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-slate-300">
                        {r.countyState || '—'}
                      </td>
                      <td className="px-3 py-2 capitalize text-gray-600 dark:text-slate-400">
                        {r.status}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-400">
              <button
                type="button"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="disabled:opacity-40 hover:underline"
              >
                Previous
              </button>
              <span>
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className="disabled:opacity-40 hover:underline"
              >
                Next
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-md border border-gray-200 dark:border-slate-700 p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                <Replace className="h-3.5 w-3.5 text-amber-500" />
                Change domain
              </h4>
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="newagency.gov"
                className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
              />
              <button
                type="button"
                onClick={applyDomain}
                disabled={!canApplyDomain}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {applyingDomain ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Replace className="h-3.5 w-3.5" />
                )}
                Apply domain
              </button>
            </div>

            <div className="rounded-md border border-gray-200 dark:border-slate-700 p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-teal-500" />
                Set County/State
              </h4>
              <input
                type="text"
                value={newCountyState}
                onChange={(e) => setNewCountyState(e.target.value)}
                placeholder="e.g. Los Angeles, CA"
                className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
              />
              <button
                type="button"
                onClick={applyCounty}
                disabled={!canApplyCounty}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {applyingCounty ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <MapPin className="h-3.5 w-3.5" />
                )}
                Apply County/State
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
